import { createApi } from '@reduxjs/toolkit/query/react';

import { LoginState, SignUpState, User } from '@typings/user';
import { authSlice } from '@redux/state/authSlice';
import { usersSlice } from '@redux/state/usersSlice';

import { dynamicBaseQuery } from '../baseQuery';

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: dynamicBaseQuery,
  endpoints: (builder) => ({
    login: builder.mutation<User, LoginState>({
      query: (body) => ({
        url: '/auth/login',
        method: 'POST',
        body,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data?.id && data?.token) {
            dispatch(authSlice.actions.setUser(data));
            // eslint-disable-next-line
            const { token, ...userData } = data;
            dispatch(usersSlice.actions.setUserDataById(userData));
          }
        } catch (error) {
          console.log('login error', error);
        }
      },
    }),
    signUp: builder.mutation<User, SignUpState>({
      query: (body) => ({
        url: '/auth/register',
        method: 'POST',
        body,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data?.id && data?.token) {
            dispatch(authSlice.actions.setUser(data));
            // eslint-disable-next-line
            const { token, ...userData } = data;
            dispatch(usersSlice.actions.setUserDataById(userData));
          }
        } catch (error) {
          console.log('signUp error', error);
        }
      },
    }),
    confirmCode: builder.mutation<void, { code: number }>({
      query: (body) => ({
        url: '/auth/code',
        method: 'POST',
        body,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(authSlice.actions.setEmailVerified(true));
        } catch (error) {
          console.log('confirmCode error', error);
        }
      },
    }),
    resendVerificationCode: builder.mutation<void, void>({
      query: () => ({
        url: '/auth/code/resend',
        method: 'POST',
      }),
    }),
    forgotPassword: builder.mutation<void, { email: string }>({
      query: (body) => ({
        url: '/auth/forgot',
        method: 'POST',
        body,
      }),
    }),
    setPassword: builder.mutation<
      User,
      { email: string; password: string; code: string }
    >({
      query: (body) => ({
        url: '/auth/password',
        method: 'POST',
        body,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data?.id && data?.token) {
            dispatch(authSlice.actions.setUser(data));
            // eslint-disable-next-line
            const { token, ...userData } = data;
            dispatch(usersSlice.actions.setUserDataById(userData));
          }
        } catch (error) {
          console.log('setPassword error', error);
        }
      },
    }),
    googleAuth: builder.mutation<User, string>({
      query: (code) => ({
        url: '/auth/google',
        method: 'POST',
        body: { code, platform: 'web' },
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data?.id && data?.token) {
            dispatch(authSlice.actions.setUser(data));
            // eslint-disable-next-line
            const { token, ...userData } = data;
            dispatch(usersSlice.actions.setUserDataById(userData));
          }
        } catch (error) {
          console.log('googleAuth error', error);
        }
      },
    }),
  }),
});

export const {
  useLoginMutation,
  useSignUpMutation,
  useConfirmCodeMutation,
  useResendVerificationCodeMutation,
  useForgotPasswordMutation,
  useSetPasswordMutation,
  useGoogleAuthMutation,
} = authApi;
